/**
 * API Composable
 * Reusable API request logic with loading and error states
 * Includes automatic token refresh on 401 errors
 */

import { ref } from 'vue';
import { API_BASE_URL } from '@/utils/constants';
import router from '@/router';
import { useAuthStore } from '@/stores/auth';

// Module-level state for token refresh coordination
let isRefreshing = false;
let refreshPromise = null;

/**
 * Attempt to refresh the access token using the refresh token
 * @returns {Promise<boolean>} - True if refresh succeeded, false otherwise
 */
async function refreshAccessToken() {
  // If already refreshing, wait for that to complete
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        console.log('[useApi] Token refreshed successfully');
        return true;
      }

      console.log('[useApi] Token refresh failed:', response.status);
      return false;
    } catch (error) {
      console.error('[useApi] Token refresh error:', error);
      return false;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

export function useApi() {
  const loading = ref(false);
  const error = ref(null);

  /**
   * Make an API request
   * @param {string} url - API endpoint URL
   * @param {object} options - Fetch options
   * @param {boolean} isRetry - Whether this is a retry after token refresh
   * @returns {Promise<any>} - Response data
   */
  async function request(url, options = {}, isRetry = false) {
    loading.value = true;
    error.value = null;

    try {
      // Check if body is FormData to handle file uploads
      const isFormData = options.body instanceof FormData;

      const config = {
        credentials: 'include',
        method: options.method || 'GET',
      };

      // Only set headers if NOT FormData - let browser set Content-Type with boundary
      if (!isFormData) {
        config.headers = {
          'Content-Type': 'application/json',
          ...options.headers,
        };
      } else if (options.headers) {
        // For FormData, only include non-Content-Type headers
        const filteredHeaders = {};
        Object.entries(options.headers).forEach(([key, value]) => {
          if (key.toLowerCase() !== 'content-type') {
            filteredHeaders[key] = value;
          }
        });
        if (Object.keys(filteredHeaders).length > 0) {
          config.headers = filteredHeaders;
        }
      }

      // Add body for POST/PUT/PATCH requests
      if (options.body) {
        if (isFormData) {
          config.body = options.body;
        } else if (typeof options.body === 'object') {
          config.body = JSON.stringify(options.body);
        } else {
          config.body = options.body;
        }
      }

      const response = await fetch(url, config);

      if (!response.ok) {
        // Handle 401 with token refresh (only if not already a retry)
        if (response.status === 401 && !isRetry) {
          // Skip refresh for auth endpoints to avoid infinite loops
          const isAuthEndpoint = url.includes('/auth/refresh') ||
                                 url.includes('/auth/logout') ||
                                 url.includes('/auth/status');

          if (!isAuthEndpoint) {
            console.log('[useApi] Got 401, attempting token refresh...');
            const refreshed = await refreshAccessToken();

            if (refreshed) {
              // Retry the original request
              console.log('[useApi] Retrying original request after token refresh');
              return await request(url, options, true);
            }
          }
        }

        await handleApiError(response);
      }

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      }

      return await response.text();
    } catch (e) {
      error.value = e.message;
      throw e;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Handle API errors
   * @param {Response} response - Fetch response object
   */
  async function handleApiError(response) {
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

    // Handle session expiration (401 Unauthorized) - only reaches here if refresh failed
    if (response.status === 401) {
      const authStore = useAuthStore();

      // Clear auth state
      authStore.$reset();

      // Redirect to login page
      if (router.currentRoute.value.path !== '/login') {
        router.push({
          path: '/login',
          query: { redirect: router.currentRoute.value.fullPath }
        });
      }

      errorMessage = 'Your session has expired. Please log in again.';
      throw new Error(errorMessage);
    }

    try {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
      } else {
        const errorText = await response.text();
        if (errorText) {
          errorMessage = errorText;
        }
      }
    } catch (parseError) {
      console.error('Error parsing error response:', parseError);
    }

    throw new Error(errorMessage);
  }

  /**
   * GET request
   * @param {string} endpoint - API endpoint (will be prefixed with API_BASE_URL)
   * @param {object} params - Query parameters
   * @param {object} options - Additional fetch options
   * @returns {Promise<any>} - Response data
   */
  async function get(endpoint, params = {}, options = {}) {
    let url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`;

    // Add query parameters if provided
    if (params && Object.keys(params).length > 0) {
      const queryString = new URLSearchParams(
        Object.entries(params).filter(([_, value]) => value !== '' && value != null)
      ).toString();

      if (queryString) {
        url += (url.includes('?') ? '&' : '?') + queryString;
      }
    }

    return request(url, { method: 'GET', ...options });
  }

  /**
   * POST request
   * @param {string} endpoint - API endpoint
   * @param {object} body - Request body
   * @param {object} options - Additional fetch options
   * @returns {Promise<any>} - Response data
   */
  async function post(endpoint, body, options = {}) {
    const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`;
    return request(url, { method: 'POST', body, ...options });
  }

  /**
   * PUT request
   * @param {string} endpoint - API endpoint
   * @param {object} body - Request body
   * @param {object} options - Additional fetch options
   * @returns {Promise<any>} - Response data
   */
  async function put(endpoint, body, options = {}) {
    const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`;
    return request(url, { method: 'PUT', body, ...options });
  }

  /**
   * PATCH request
   * @param {string} endpoint - API endpoint
   * @param {object} body - Request body
   * @param {object} options - Additional fetch options
   * @returns {Promise<any>} - Response data
   */
  async function patch(endpoint, body, options = {}) {
    const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`;
    return request(url, { method: 'PATCH', body, ...options });
  }

  /**
   * DELETE request
   * @param {string} endpoint - API endpoint
   * @param {object} body - Request body (optional)
   * @param {object} options - Additional fetch options
   * @returns {Promise<any>} - Response data
   */
  async function del(endpoint, body = null, options = {}) {
    const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`;
    return request(url, { method: 'DELETE', body, ...options });
  }

  return {
    loading,
    error,
    request,
    get,
    post,
    put,
    patch,
    del,
    delete: del,
  };
}
